import React, { useEffect, useState } from "react";
import { Moon, Sun, Monitor } from "lucide-react";
import { Button } from "./ui/button";

export function ThemeToggle() {
  const [theme, setTheme] = useState<"light" | "dark" | "system">("dark");
  const [mounted, setMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setMounted(true);

    // Check for theme in URL parameters first (takes precedence)
    const urlParams = new URLSearchParams(window.location.search);
    const urlTheme = urlParams.get("theme") as "light" | "dark" | "system";

    if (urlTheme && ["light", "dark", "system"].includes(urlTheme)) {
      setTheme(urlTheme);
      localStorage.setItem("saas-invest-theme", urlTheme);
      applyTheme(urlTheme);
      return;
    }

    // Load theme from localStorage
    const savedTheme = localStorage.getItem("saas-invest-theme") as "light" | "dark" | "system";
    if (savedTheme) {
      setTheme(savedTheme);
      applyTheme(savedTheme);
    } else {
      // Default to dark
      setTheme("dark");
      applyTheme("dark");
    }
  }, []);

  const applyTheme = (newTheme: "light" | "dark" | "system") => {
    const root = window.document.documentElement;

    root.classList.remove("light", "dark");

    if (newTheme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
      root.classList.add(systemTheme);
    } else {
      root.classList.add(newTheme);
    }

    console.log("Applied theme:", newTheme, "classes:", root.className);
  };

  const handleThemeChange = (newTheme: "light" | "dark" | "system") => {
    setTheme(newTheme);
    localStorage.setItem("saas-invest-theme", newTheme);
    applyTheme(newTheme);
    setIsOpen(false);
  };

  const getIcon = () => {
    if (theme === "dark") return <Sun className="h-4 w-4" />;
    if (theme === "system") return <Monitor className="h-4 w-4" />;
    return <Moon className="h-4 w-4" />;
  };

  const getLabel = () => {
    if (theme === "dark") return "Dark";
    if (theme === "system") return "System";
    return "Light";
  };

  if (!mounted) {
    return (
      <Button variant="outline" size="sm" className="w-[100px] justify-start gap-2">
        <Sun className="h-4 w-4" />
        <span>Dark</span>
      </Button>
    );
  }

  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="w-[100px] justify-start gap-2"
      >
        {getIcon()}
        <span className="truncate">{getLabel()}</span>
      </Button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 top-full mt-1 z-20 w-[120px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg">
            <div className="p-1">
              <button
                onClick={() => handleThemeChange("light")}
                className="w-full flex items-center gap-2 px-2 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded-sm transition-colors text-gray-900 dark:text-gray-100"
              >
                <Moon className="h-4 w-4" />
                <span>Light</span>
              </button>
              <button
                onClick={() => handleThemeChange("dark")}
                className="w-full flex items-center gap-2 px-2 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded-sm transition-colors text-gray-900 dark:text-gray-100"
              >
                <Sun className="h-4 w-4" />
                <span>Dark</span>
              </button>
              <button
                onClick={() => handleThemeChange("system")}
                className="w-full flex items-center gap-2 px-2 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded-sm transition-colors text-gray-900 dark:text-gray-100"
              >
                <Monitor className="h-4 w-4" />
                <span>System</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
